import { giphyWhite } from '@giphy/js-brand'
import { IGif } from '@giphy/js-types'
import React, { useContext, useEffect, useState } from 'react'
import { searchClips } from 'shared/api'
import SharedCarousel from 'shared/components/carousel/carousel'
import SearchOptionsContext from 'shared/components/context/search-options'
import SharedVideoPreviewPlayInline from 'shared/components/video-inline-play'
import SharedVideoPreviewThumbnail from 'shared/components/video-preview-thumbnail'
import { VideoOverlayContext } from 'shared/components/video-inline-play/video'
import { gridWidth } from 'shared/util/grid'
import isMobile from 'shared/util/is-mobile'
import { Experiment, Variant } from 'react-optimize'
import styled, { keyframes } from 'styled-components'
import VideoPreviewCarousel from 'mobile/components/video-carousel'
import { getPingbackId } from '@giphy/js-util'

const MARGIN = 16
const MAX_CAROUSEL_ITEMS = 4

const channelIn = keyframes`
    0% {
        opacity: 0;
        transform: translateY(60px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
`

const ShowClips = styled.div`
    display: block;
`

const GridSectionHeader = styled.div`
    display: flex;
    justify-content: flex-start;
    padding: 0 5px;
    box-sizing: border-box;
    margin-top: 7px;
    font-family: InterFace;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0.25px;
`

const Carousel = styled(SharedCarousel)`
    margin-bottom: 15px;
    & h2 {
        color: ${giphyWhite};
        font-size: 17px;
        font-weight: bold;
        letter-spacing: 0.3px;
        font-family: InterFace;
    }
    & h3 {
        font-family: interface;
        font-weight: bold;
        box-shadow: 0px 2px 24px 0px rgba(0, 0, 0, 0.25);
    }
`

const VideoStyle = `
    display: inline-block;
    margin-bottom: ${MARGIN}px;
    margin-right: ${MARGIN}px;
    &:last-child {
        margin-right: 0;
    }
    
    &:nth-child(2n) {
        animation-delay: 0.05s;
    }

    &:nth-child(3n) {
        animation-delay: 0.1s;
    }

    &:nth-child(4n) {
        animation-delay: 0.15s;
    }

    &:last-of-type {
        margin: 0px;
    }
`

const VideoPreviewPlayInline = styled(SharedVideoPreviewPlayInline)`
    animation: ${channelIn} 0.3s ease-in-out 0s 1 normal backwards;
    ${VideoStyle}
`

const VideoPreviewThumbnail = styled(SharedVideoPreviewThumbnail)`
    animation: ${channelIn} 0.3s ease-in-out 0s 1 normal backwards;
    ${VideoStyle}
`

const getItemWidth = (width: number) => {
    const margin = (MAX_CAROUSEL_ITEMS - 1) * MARGIN
    return Math.floor((width - margin) / MAX_CAROUSEL_ITEMS)
}

const DESKTOP_EXPERIMENT_ID = '2pA4t1gzTsyNeT_MFmflVQ'

interface Props {
    term: string
}

const ClipsCarousel = ({ term }: Props): JSX.Element => {
    const { setClipSearchResponseId } = useContext(SearchOptionsContext)
    const [clips, setClips] = useState<IGif[]>([])
    const [hoveredClip, setHoveredClip] = useState<null | string>(null)
    const [finishVolumeIconAnimation, setFinishVolumeIconAnimation] = useState(false)
    const itemWidth = getItemWidth(gridWidth)
    const pingbackId = getPingbackId()

    const fetchClips = async (term: string, pingbackId: string) => {
        const { data, meta } = await searchClips({ q: term, limit: 20, pingbackId: pingbackId })
        setClips(data)
        setClipSearchResponseId(meta.response_id)
    }

    useEffect(() => {
        !clips.length && fetchClips(term, pingbackId)
    }, [])

    return (
        <ShowClips>
            {!!clips.length &&
                (isMobile() ? (
                    <>
                        <GridSectionHeader>GIPHY Clips</GridSectionHeader>
                        <VideoPreviewCarousel previews={clips} isSearch />
                    </>
                ) : (
                    <>
                        <VideoOverlayContext.Provider
                            value={{
                                onStartHover: (gifId: string) => setHoveredClip(gifId),
                                onFinishHover: () => setHoveredClip(null),
                                onFinishVolumeIconAnimation: () => setFinishVolumeIconAnimation(true),
                                finishVolumeIconAnimation,
                            }}
                        >
                            <Carousel maxBlocks={MAX_CAROUSEL_ITEMS} title={`GIPHY Clips`}>
                                {clips.map((gif: IGif) => (
                                    <Experiment id={DESKTOP_EXPERIMENT_ID} key={gif.id}>
                                        <Variant id="0">
                                            <VideoPreviewThumbnail
                                                gif={gif}
                                                width={itemWidth}
                                                hideAttribution={true}
                                                preventDefault={false}
                                                hideLabel
                                                playButtonAnimated={true}
                                            />
                                        </Variant>
                                        <Variant id="1">
                                            <VideoPreviewThumbnail
                                                gif={gif}
                                                width={itemWidth}
                                                hideAttribution={true}
                                                preventDefault={false}
                                                hideLabel={false}
                                                playButtonAnimated={true}
                                            />
                                        </Variant>
                                        <Variant id="2">
                                            <VideoPreviewPlayInline
                                                gif={gif}
                                                width={itemWidth}
                                                withOverlay={hoveredClip !== gif.id && hoveredClip !== null}
                                                showClipTitle={false}
                                            />
                                        </Variant>
                                        <Variant id="3">
                                            <VideoPreviewPlayInline
                                                gif={gif}
                                                width={itemWidth}
                                                withOverlay={hoveredClip !== gif.id && hoveredClip !== null}
                                                showClipTitle={true}
                                            />
                                        </Variant>
                                        <Variant id="4">
                                            <VideoPreviewPlayInline
                                                gif={gif}
                                                width={itemWidth}
                                                showClipTitle={true}
                                                playOnHover={false}
                                            />
                                        </Variant>
                                        <Variant id="5">
                                            <VideoPreviewPlayInline
                                                gif={gif}
                                                width={itemWidth}
                                                showClipTitle={false}
                                                playOnHover={false}
                                            />
                                        </Variant>
                                    </Experiment>
                                ))}
                            </Carousel>
                        </VideoOverlayContext.Provider>
                    </>
                ))}
        </ShowClips>
    )
}

export default ClipsCarousel
